/**
 * History & Library Panel Component
 * Right panel with tabs for history and library (MinIO storage)
 * Matches the old admin playground layout
 */

import { useState } from 'react';
import {
  History,
  FolderOpen,
  RefreshCw,
  Trash2,
  Eye,
  Download,
  FolderPlus,
  GitBranch,
  Image as ImageIcon,
} from 'lucide-react';
import { useTasks, useClearTasks, useLibrary, useSaveTask, useDeleteTask, useDeleteFromLibrary } from '../hooks/usePlayground';
import { playgroundApi } from '@/lib/api/playground';
import { IMAGE_MODELS, type ImageTask, type LibraryItem } from '@/types/playground';
import type { OperationMode } from '@/pages/PlaygroundPage';

interface SelectedImage {
  id: string;
  image_url: string;
  source: 'history' | 'library' | 'upload';
  base64?: string;
}

interface HistoryLibraryPanelProps {
  operationMode: OperationMode;
  onModeChange: (mode: OperationMode) => void;
  selectedImageForEdit: SelectedImage | null;
  onSelectImageForEdit: (image: SelectedImage | null) => void;
  selectedImagesForFuse: SelectedImage[];
  onSelectImagesForFuse: (images: SelectedImage[]) => void;
}

type PanelTab = 'history' | 'library';

export function HistoryLibraryPanel({
  operationMode,
  onModeChange,
  selectedImageForEdit,
  onSelectImageForEdit,
  selectedImagesForFuse,
  onSelectImagesForFuse,
}: HistoryLibraryPanelProps) {
  const [activePanel, setActivePanel] = useState<PanelTab>('history');
  const [viewingImage, setViewingImage] = useState<(ImageTask | LibraryItem) & { source: 'history' | 'library' } | null>(null);
  const [savingToLibrary, setSavingToLibrary] = useState(false);

  // Queries
  const { data: tasksData, isLoading: historyLoading, refetch: refetchHistory } = useTasks({ limit: 100 });
  const { data: libraryData, isLoading: libraryLoading, refetch: refetchLibrary } = useLibrary({ limit: 100 });

  // Mutations
  const clearTasksMutation = useClearTasks();
  const saveTaskMutation = useSaveTask();
  const deleteTaskMutation = useDeleteTask();
  const deleteFromLibraryMutation = useDeleteFromLibrary();

  const history = tasksData?.tasks || [];
  const libraryFiles = libraryData?.items || [];

  // Operation type badges
  const getOperationColor = (operation: string) => {
    switch (operation) {
      case 'generate':
        return 'bg-green-100 text-green-800';
      case 'edit':
        return 'bg-blue-100 text-blue-800';
      case 'fuse':
        return 'bg-purple-100 text-purple-800';
      case 'lucky_charm':
        return 'bg-amber-100 text-amber-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getOperationLabel = (operation: string) => {
    switch (operation) {
      case 'generate':
        return '生成';
      case 'edit':
        return '编辑';
      case 'fuse':
        return '融合';
      case 'lucky_charm':
        return '幸运符';
      default:
        return operation;
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp: string | undefined) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    // Check for invalid date
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleString('zh-CN', {
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handlers
  const handleClearHistory = async () => {
    if (confirm('确定要清空所有历史记录吗？此操作不可撤销。')) {
      await clearTasksMutation.mutateAsync();
    }
  };

  const handleViewImage = (item: ImageTask | LibraryItem, source: 'history' | 'library') => {
    setViewingImage({ ...item, source });
  };

  const handleDownload = async (item: ImageTask | LibraryItem) => {
    const url = 'result' in item ? item.result?.image_url : (item as LibraryItem).url;
    if (url) {
      await playgroundApi.utils.downloadImage(url, `image-${item.id}.png`);
    }
  };

  const handleSaveToLibrary = async (task: ImageTask) => {
    // Use task_id for direct save - no re-download needed!
    const taskId = task.task_id || task.id;
    if (!taskId) return;

    setSavingToLibrary(true);
    try {
      await saveTaskMutation.mutateAsync({
        taskId,
        category: 'generated',
        filename: `${task.type}-${taskId}.png`,
        metadata: {
          prompt: task.prompt,
          operation: task.type,
          model: task.config?.model || task.model,
        },
      });
      alert('已保存到图库');
    } catch (error) {
      console.error('Failed to save to library:', error);
      alert('保存失败');
    } finally {
      setSavingToLibrary(false);
    }
  };

  const handleDeleteTask = async (task: ImageTask) => {
    const taskId = task.task_id || task.id;
    if (!taskId) return;

    if (confirm('确定要删除此历史记录吗？')) {
      try {
        await deleteTaskMutation.mutateAsync(taskId);
        // Close modal if viewing this task
        if (viewingImage && 'task_id' in viewingImage && (viewingImage.task_id === taskId || viewingImage.id === taskId)) {
          setViewingImage(null);
        }
      } catch (error) {
        console.error('Failed to delete task:', error);
        alert('删除失败');
      }
    }
  };

  const handleDeleteFromLibrary = async (item: LibraryItem) => {
    if (confirm('确定要从图库中删除此图像吗？')) {
      await deleteFromLibraryMutation.mutateAsync(item.id);
    }
  };

  // Selection for edit/fuse modes
  const selectForEdit = (item: ImageTask | LibraryItem, source: 'history' | 'library') => {
    const url = source === 'history'
      ? ((item as ImageTask).result?.image_url || (item as ImageTask).temp_url)
      : (item as LibraryItem).url;
    if (url) {
      onSelectImageForEdit({
        id: item.id,
        image_url: url,
        source,
      });
      // Auto switch to edit mode
      onModeChange('edit');
    }
  };

  const toggleForFuse = (item: ImageTask | LibraryItem, source: 'history' | 'library') => {
    const url = source === 'history' ? (item as ImageTask).result?.image_url : (item as LibraryItem).url;
    if (!url) return;

    const existing = selectedImagesForFuse.find((img) => img.id === item.id);
    if (existing) {
      onSelectImagesForFuse(selectedImagesForFuse.filter((img) => img.id !== item.id));
    } else {
      // Check max limit (default to 14 for PRO model)
      if (selectedImagesForFuse.length >= 14) return;
      onSelectImagesForFuse([
        ...selectedImagesForFuse,
        { id: item.id, image_url: url, source },
      ]);
    }
  };

  const isSelectedForFuse = (id: string) => selectedImagesForFuse.some((img) => img.id === id);

  return (
    <div className="p-6">
      {/* Panel Tabs */}
      <div className="flex gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
        <button
          onClick={() => setActivePanel('history')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            activePanel === 'history' ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <History className="w-4 h-4" />
          <span>历史记录</span>
          {history.length > 0 && (
            <span className="text-xs text-gray-500">({history.length})</span>
          )}
        </button>
        <button
          onClick={() => setActivePanel('library')}
          className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all flex items-center justify-center gap-2 ${
            activePanel === 'library' ? 'bg-white shadow text-indigo-600' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <FolderOpen className="w-4 h-4" />
          <span>图库</span>
          {libraryFiles.length > 0 && (
            <span className="text-xs text-gray-500">({libraryFiles.length})</span>
          )}
        </button>
      </div>

      {/* History Panel Header */}
      {activePanel === 'history' && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <History className="w-5 h-5 text-gray-500" />
            历史记录
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => refetchHistory()}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              刷新
            </button>
            {history.length > 0 && (
              <button
                onClick={handleClearHistory}
                disabled={clearTasksMutation.isPending}
                className="px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 inline mr-1" />
                清空
              </button>
            )}
          </div>
        </div>
      )}

      {/* Library Panel Header */}
      {activePanel === 'library' && (
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-indigo-500" />
            图库 (MinIO)
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => refetchLibrary()}
              disabled={libraryLoading}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 inline mr-1 ${libraryLoading ? 'animate-spin' : ''}`} />
              刷新
            </button>
          </div>
        </div>
      )}

      {/* History Empty State */}
      {activePanel === 'history' && !historyLoading && history.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <ImageIcon className="w-12 h-12 mx-auto mb-4" />
          <p className="text-lg font-medium">还没有生成图像</p>
          <p className="text-sm mt-1">生成的图像将显示在这里</p>
        </div>
      )}

      {/* Library Empty State */}
      {activePanel === 'library' && !libraryLoading && libraryFiles.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <FolderOpen className="w-12 h-12 mx-auto mb-4" />
          <p className="text-lg font-medium">图库为空</p>
          <p className="text-sm mt-1">保存生成的图像到图库进行持久化存储</p>
        </div>
      )}

      {/* Loading State */}
      {((activePanel === 'history' && historyLoading) || (activePanel === 'library' && libraryLoading)) && (
        <div className="text-center py-16 text-gray-400">
          <RefreshCw className="w-12 h-12 mx-auto mb-4 animate-spin" />
          <p className="text-lg font-medium">加载中...</p>
        </div>
      )}

      {/* History Grid */}
      {activePanel === 'history' && history.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {history.map((task) => (
            <div
              key={task.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
            >
              {/* Image */}
              <div
                className="relative aspect-[3/4] cursor-pointer"
                onClick={() => handleViewImage(task, 'history')}
              >
                {task.result?.image_url ? (
                  <img
                    src={task.result.image_url}
                    alt={task.prompt}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                {/* Operation Badge */}
                <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium ${getOperationColor(task.type)}`}>
                  {getOperationLabel(task.type)}
                </span>
                {/* Saved Badge */}
                {task.status === 'saved' && (
                  <span className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                    已保存
                  </span>
                )}
                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleViewImage(task, 'history'); }}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                    title="查看"
                  >
                    <Eye className="w-4 h-4 text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDownload(task); }}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                    title="下载"
                  >
                    <Download className="w-4 h-4 text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleSaveToLibrary(task); }}
                    disabled={savingToLibrary || task.status === 'saved'}
                    className={`p-2 bg-white rounded-full hover:bg-gray-100 disabled:opacity-50 ${task.status === 'saved' ? 'cursor-not-allowed' : ''}`}
                    title={task.status === 'saved' ? '已保存到图库' : '保存到图库'}
                  >
                    <FolderPlus className={`w-4 h-4 ${task.status === 'saved' ? 'text-gray-400' : 'text-indigo-600'} ${savingToLibrary ? 'animate-spin' : ''}`} />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); selectForEdit(task, 'history'); }}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                    title="编辑/分叉"
                  >
                    <GitBranch className="w-4 h-4 text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteTask(task); }}
                    disabled={deleteTaskMutation.isPending}
                    className="p-2 bg-white rounded-full hover:bg-red-50 disabled:opacity-50"
                    title="删除"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
                {/* Selection Indicator for Edit */}
                {operationMode === 'edit' && selectedImageForEdit?.id === task.id && (
                  <div className="absolute inset-0 border-4 border-blue-500 pointer-events-none" />
                )}
                {/* Selection Indicator for Fuse */}
                {operationMode === 'fuse' && isSelectedForFuse(task.id) && (
                  <div className="absolute inset-0 border-4 border-purple-500 pointer-events-none">
                    <span className="absolute top-2 right-2 bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {selectedImagesForFuse.findIndex((img) => img.id === task.id) + 1}
                    </span>
                  </div>
                )}
              </div>
              {/* Info */}
              <div className="p-3">
                <p className="text-xs text-gray-500 truncate">{task.prompt}</p>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                  <span>
                    {IMAGE_MODELS.find((m) => m.value === task.config.model)?.label || task.config.model}
                  </span>
                  <span>{task.created_at ? formatTimestamp(task.created_at) : '-'}</span>
                </div>
                {/* Select Button for Edit/Fuse Modes */}
                {(operationMode === 'edit' || operationMode === 'fuse') && (
                  <div className="mt-2">
                    {operationMode === 'edit' && (
                      <button
                        onClick={() => selectForEdit(task, 'history')}
                        className={`w-full py-1.5 text-xs font-medium rounded border transition-colors ${
                          selectedImageForEdit?.id === task.id
                            ? 'bg-blue-100 text-blue-700 border-blue-500'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:border-blue-500'
                        }`}
                      >
                        {selectedImageForEdit?.id === task.id ? '已选择' : '选择编辑'}
                      </button>
                    )}
                    {operationMode === 'fuse' && (
                      <button
                        onClick={() => toggleForFuse(task, 'history')}
                        className={`w-full py-1.5 text-xs font-medium rounded border transition-colors ${
                          isSelectedForFuse(task.id)
                            ? 'bg-purple-100 text-purple-700 border-purple-500'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:border-purple-500'
                        }`}
                      >
                        {isSelectedForFuse(task.id) ? '取消选择' : '选择融合'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Library Grid */}
      {activePanel === 'library' && libraryFiles.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {libraryFiles.map((file) => (
            <div
              key={file.id}
              className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group"
            >
              {/* Image */}
              <div
                className="relative aspect-[3/4] cursor-pointer"
                onClick={() => handleViewImage(file, 'library')}
              >
                {file.url ? (
                  <img
                    src={file.thumbnail_url || file.url}
                    alt={file.filename}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-100">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                )}
                {/* Library Badge */}
                <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                  <FolderOpen className="w-3 h-3 inline mr-1" />
                  图库
                </span>
                {/* Hover Actions */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleViewImage(file, 'library'); }}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                    title="查看"
                  >
                    <Eye className="w-4 h-4 text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                    title="下载"
                  >
                    <Download className="w-4 h-4 text-gray-700" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteFromLibrary(file); }}
                    className="p-2 bg-white rounded-full hover:bg-gray-100"
                    title="删除"
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </button>
                </div>
                {/* Selection Indicator for Edit */}
                {operationMode === 'edit' && selectedImageForEdit?.id === file.id && (
                  <div className="absolute inset-0 border-4 border-blue-500 pointer-events-none" />
                )}
                {/* Selection Indicator for Fuse */}
                {operationMode === 'fuse' && isSelectedForFuse(file.id) && (
                  <div className="absolute inset-0 border-4 border-purple-500 pointer-events-none">
                    <span className="absolute top-2 right-2 bg-purple-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                      {selectedImagesForFuse.findIndex((img) => img.id === file.id) + 1}
                    </span>
                  </div>
                )}
              </div>
              {/* Info */}
              <div className="p-3">
                <p className="text-xs text-gray-500 truncate">
                  {(file.metadata as { prompt?: string })?.prompt || file.filename}
                </p>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                  <span>
                    {(file.metadata as { model?: string })?.model
                      ? IMAGE_MODELS.find((m) => m.value === (file.metadata as { model?: string }).model)?.label ||
                        (file.metadata as { model?: string }).model
                      : '-'}
                  </span>
                  <span>{formatTimestamp(file.created_at)}</span>
                </div>
                {/* Select Button for Edit/Fuse Modes */}
                {(operationMode === 'edit' || operationMode === 'fuse') && (
                  <div className="mt-2">
                    {operationMode === 'edit' && (
                      <button
                        onClick={() => selectForEdit(file, 'library')}
                        className={`w-full py-1.5 text-xs font-medium rounded border transition-colors ${
                          selectedImageForEdit?.id === file.id
                            ? 'bg-blue-100 text-blue-700 border-blue-500'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:border-blue-500'
                        }`}
                      >
                        {selectedImageForEdit?.id === file.id ? '已选择' : '选择编辑'}
                      </button>
                    )}
                    {operationMode === 'fuse' && (
                      <button
                        onClick={() => toggleForFuse(file, 'library')}
                        className={`w-full py-1.5 text-xs font-medium rounded border transition-colors ${
                          isSelectedForFuse(file.id)
                            ? 'bg-purple-100 text-purple-700 border-purple-500'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:border-purple-500'
                        }`}
                      >
                        {isSelectedForFuse(file.id) ? '取消选择' : '选择融合'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Image Preview Modal */}
      {viewingImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={() => setViewingImage(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative bg-white rounded-lg shadow-2xl max-w-4xl max-h-[90vh] overflow-auto"
          >
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ImageIcon className="w-5 h-5 text-primary-600" />
                图像预览
                {'type' in viewingImage && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getOperationColor(viewingImage.type)}`}>
                    {getOperationLabel(viewingImage.type)}
                  </span>
                )}
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    selectForEdit(viewingImage, viewingImage.source);
                    setViewingImage(null);
                  }}
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <GitBranch className="w-4 h-4" />
                  编辑/分叉
                </button>
                <button
                  onClick={() => handleDownload(viewingImage)}
                  className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  下载
                </button>
                {viewingImage.source === 'history' && (
                  <>
                    <button
                      onClick={() => handleSaveToLibrary(viewingImage as ImageTask)}
                      disabled={savingToLibrary || (viewingImage as ImageTask).status === 'saved'}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 ${
                        (viewingImage as ImageTask).status === 'saved'
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      <FolderPlus className={`w-4 h-4 ${savingToLibrary ? 'animate-spin' : ''}`} />
                      {(viewingImage as ImageTask).status === 'saved' ? '已保存' : savingToLibrary ? '保存中...' : '保存到图库'}
                    </button>
                    <button
                      onClick={() => handleDeleteTask(viewingImage as ImageTask)}
                      disabled={deleteTaskMutation.isPending}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      删除
                    </button>
                  </>
                )}
                <button
                  onClick={() => setViewingImage(null)}
                  className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  关闭
                </button>
              </div>
            </div>

            {/* Image */}
            <div className="p-6">
              <img
                src={
                  viewingImage.source === 'history'
                    ? (viewingImage as ImageTask).result?.image_url
                    : (viewingImage as LibraryItem).url
                }
                alt="Preview"
                className="w-full h-auto rounded-lg border border-gray-200"
              />

              {/* Image Info */}
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">模型:</span>
                    <span className="ml-2 text-gray-900">
                      {viewingImage.source === 'history'
                        ? IMAGE_MODELS.find((m) => m.value === (viewingImage as ImageTask).config?.model)?.label ||
                          (viewingImage as ImageTask).config?.model ||
                          (viewingImage as ImageTask).model ||
                          '-'
                        : ((viewingImage as LibraryItem).metadata as { model?: string })?.model || '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">时间:</span>
                    <span className="ml-2 text-gray-900">
                      {viewingImage.created_at ? formatTimestamp(viewingImage.created_at) : '-'}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">来源:</span>
                    <span
                      className={`ml-2 ${viewingImage.source === 'library' ? 'text-indigo-600' : 'text-gray-900'}`}
                    >
                      {viewingImage.source === 'library' ? '图库 (MinIO)' : '历史记录'}
                    </span>
                  </div>
                </div>
                <div className="mt-3">
                  <span className="text-gray-500 text-sm">描述:</span>
                  <p className="mt-1 text-gray-700 text-sm whitespace-pre-wrap">
                    {viewingImage.source === 'history'
                      ? (viewingImage as ImageTask).prompt || '-'
                      : ((viewingImage as LibraryItem).metadata as { prompt?: string })?.prompt || (viewingImage as LibraryItem).filename || '-'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
